import { useState } from 'react';

import { useSelectionParam } from '../../hooks/navigation/useUrlState';
import {
  downloadNdaArtifact,
  downloadNdaDocument,
  useArchiveReview,
  useConfirmExecution,
  useConfirmObligations,
  useEditObligation,
  useNdaDocket,
  useNdaErrors,
  useNdaExecution,
  useNdaHealth,
  useNdaReviewDetail,
  useNdaSettings,
  useRevertExecution,
  useRevertObligation,
  useUnbindReview,
  useUpdateNdaSettings,
  useUpdateReviewLinks,
  useUpdateReviewStatus,
} from '../../hooks/useNda';
import { NdaBindModal } from './NdaBindModal';
import { NdaDecisionQueue } from './NdaDecisionQueue';
import { NdaIntakeDecisions } from './NdaIntakeDecisions';
import { NdaOrgPickModal } from './NdaOrgPickModal';
import type {
  NdaArtifactRow,
  NdaCaptureConfig,
  NdaCaptureConfigUpdate,
  NdaDeliveryState,
  NdaDocketRow,
  NdaDocument,
  NdaErrorEntry,
  NdaFindingRow,
  NdaObligationEdit,
  NdaObligationRow,
  NdaReviewDetail,
  NdaTimelineEntry,
  NdaWaitingOn,
} from '../../types';
import { splitRecommendation, wordDiff } from '../../utils/findings';
import { mustHaveProgressLabel, waitingOnLabel } from '../../utils/ndaLabels';
import { deepLinks } from '../../utils/deepLinks';
import styles from './NdaPage.module.css';

/**
 * NDA tab (build plan 1.11, spec §12.1) — Peter/Brad-only docket.
 *
 * Read-only transparency surface in Phase 1: what NDAs are in flight,
 * what's waiting on whom, and what the system has done. NOT a legal
 * editor — the email loop remains the workflow. Server-gated to
 * nda_privileged_emails on every /api/nda/* route; the tab itself is
 * allowlist-only in /api/tabs.
 */
export function NdaPage() {
  // `?review=N` is a real, shareable, reload-stable deep link (spec §5.1). The
  // shared useSelectionParam hook handles open (push + marker), close
  // (history.back when we own the entry, else drop the param for an
  // externally-arrived link), and popstate resync. The old strip-on-mount
  // workaround is gone: plain tab switches now drop all query params
  // (useTabRouter §3.4), so a lingering ?review can no longer leak across tabs.
  const { value: reviewParam, open, close } = useSelectionParam('review');
  const selectedId =
    reviewParam != null && /^\d+$/.test(reviewParam) ? parseInt(reviewParam, 10) : null;

  if (selectedId != null) {
    return <ReviewDetail reviewId={selectedId} onBack={close} />;
  }
  return <Docket onSelect={(id: number) => open(String(id))} />;
}

// ── Docket ───────────────────────────────────────────────────────────────────

function Docket({ onSelect }: { onSelect: (id: number) => void }) {
  const docket = useNdaDocket();
  const archive = useArchiveReview();

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>NDA Docket</h1>
        <span className={styles.subtitle}>
          privileged · read-only · the email loop remains the workflow
        </span>
      </div>

      <HealthStrip />
      <NdaDecisionQueue />
      <NdaIntakeDecisions />
      <GcPolicyControl />
      <CaptureSettingsPanel />

      {docket.isLoading && <div className={styles.loading}>Loading docket…</div>}
      {docket.error && (
        <div className={styles.error}>
          Could not load the docket: {docket.error.message}
        </div>
      )}
      {docket.data && docket.data.rows.length === 0 && (
        <div className={styles.empty}>
          No active reviews. Forward an NDA to nda@ember-infra.com to open one.
        </div>
      )}
      {docket.data && docket.data.rows.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Deal</th>
              <th>Status</th>
              <th>Waiting on</th>
              <th>Delivery</th>
              <th>Turn</th>
              <th>Must-haves</th>
              <th>Age</th>
              <th>Last activity</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {docket.data.rows.map((row) => (
              <DocketRow
                key={row.id}
                row={row}
                onSelect={onSelect}
                onArchive={(id) => archive.mutate({ id, archived: true })}
              />
            ))}
          </tbody>
        </table>
      )}

      {docket.data && docket.data.archived_rows.length > 0 && (
        <ArchivedPanel
          rows={docket.data.archived_rows}
          onSelect={onSelect}
          onUnarchive={(id) => archive.mutate({ id, archived: false })}
        />
      )}

      <ErrorPanel />
    </div>
  );
}

// ── GC auto-inclusion policy control (PR 6, spec §0.4) ────────────────────────

const GC_POLICY_ORDER = ['forwarder_only', 'on_thread', 'always'];
const GC_POLICY_LABEL: Record<string, string> = {
  forwarder_only: 'Forwarder only — Brad only on auto-detected',
  on_thread: 'On thread — add the GC when a To/Cc of the trigger',
  always: 'Always — add the GC to every auto-detected packet',
};

function GcPolicyControl() {
  const settings = useNdaSettings();
  const update = useUpdateNdaSettings();
  const current = settings.data?.gc_auto_inclusion;
  if (!current) return null; // hidden when unavailable (e.g. non-privileged 403)

  const onChange = (next: string) => {
    if (next === current) return;
    const escalating =
      GC_POLICY_ORDER.indexOf(next) > GC_POLICY_ORDER.indexOf(current);
    if (
      escalating &&
      !window.confirm(
        'This widens who receives auto-detected NDA reviews (the GC is added). Continue?',
      )
    ) {
      return;
    }
    update.mutate({ gc_auto_inclusion: next });
  };

  return (
    <div className={styles.metaRow}>
      <span className={styles.metaItem}>GC auto-inclusion</span>
      <select
        value={current}
        disabled={update.isPending}
        onChange={(e) => onChange(e.target.value)}
      >
        {GC_POLICY_ORDER.map((p) => (
          <option key={p} value={p}>
            {GC_POLICY_LABEL[p]}
          </option>
        ))}
      </select>
      {settings.data?.updated_by && (
        <span className={styles.metaItem}>set by {settings.data.updated_by}</span>
      )}
      {update.isError && (
        <span className={styles.error}>Could not update the policy — try again.</span>
      )}
    </div>
  );
}

// ── Capture / notification settings (PR-3, collapsed by default) ──────────────

const SCAN_TOGGLES: { key: keyof NdaCaptureConfig; label: string; hint: string }[] = [
  { key: 'scan_outbound_enabled', label: 'Scan outbound drafts',
    hint: 'Capture NDA drafts Ember sends out (binds to the matter).' },
  { key: 'scan_inbound_enabled', label: 'Scan inbound revisions',
    hint: 'Log counterparty revisions off the mail spine (no auto-review).' },
  { key: 'scan_execution_enabled', label: 'Scan for execution copies',
    hint: 'Detect executed copies for term extraction.' },
];

const NOTIFY_TOGGLES: { key: keyof NdaCaptureConfig; label: string }[] = [
  { key: 'notify_inbound_turn', label: 'Counterparty revision captured' },
  { key: 'notify_outbound_capture', label: 'Ember draft captured' },
  { key: 'notify_execution_candidate', label: 'Execution copy detected' },
  { key: 'notify_terms_extracted', label: 'Executed terms extracted' },
];

function ToggleRow({
  label, hint, value, disabled, onChange,
}: {
  label: string; hint?: string; value: boolean; disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
      <input
        type="checkbox"
        checked={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {hint && (
          <span style={{ display: 'block', fontSize: 12, color: '#888' }}>{hint}</span>
        )}
      </span>
    </label>
  );
}

function CaptureSettingsPanel() {
  const [open, setOpen] = useState(false);
  const settings = useNdaSettings();
  const update = useUpdateNdaSettings();
  const cap = settings.data?.capture;

  // local editors for the free-text list/number fields (explicit Save). Synced
  // from the server value during render, re-syncing only when the server object
  // reference changes — React Query's structural sharing keeps it stable when the
  // content is unchanged, so a background refetch won't clobber an in-flight edit.
  const [syncedFrom, setSyncedFrom] = useState<NdaCaptureConfig | null>(null);
  const [recipients, setRecipients] = useState('');
  const [mailboxes, setMailboxes] = useState('');
  const [lookback, setLookback] = useState('');
  if (cap && cap !== syncedFrom) {
    setSyncedFrom(cap);
    setRecipients(cap.notify_recipients.join(', '));
    setMailboxes(cap.authoritative_mailboxes.join(', '));
    setLookback(String(cap.turn_scan_lookback_days));
  }

  if (!cap) return null; // hidden when unavailable (non-privileged 403 / older API)

  const setToggle = (key: keyof NdaCaptureConfig, next: boolean) => {
    // Enabling a scan arm widens system behavior + LLM/graph spend — confirm.
    if (next && key.startsWith('scan_') &&
        !window.confirm('This enables live scanning of the mailbox for NDA activity. Continue?')) {
      return;
    }
    update.mutate({ capture: { [key]: next } as NdaCaptureConfigUpdate });
  };

  const parseList = (raw: string) =>
    raw.split(',').map((s) => s.trim()).filter(Boolean);

  const saveLists = () => {
    update.mutate({
      capture: {
        notify_recipients: parseList(recipients),
        authoritative_mailboxes: parseList(mailboxes),
        turn_scan_lookback_days: Math.max(1, parseInt(lookback, 10) || 1),
      },
    });
  };

  return (
    <div className={styles.errorPanel}>
      <button
        type="button"
        className={styles.errorPanelToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        Capture &amp; notifications
        <span className={styles.errorPanelChevron}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div style={{ padding: '8px 12px', display: 'grid', gap: 16 }}>
          <section>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Scanning</div>
            {SCAN_TOGGLES.map((t) => (
              <ToggleRow
                key={t.key}
                label={t.label}
                hint={t.hint}
                value={cap[t.key] as boolean}
                disabled={update.isPending}
                onChange={(next) => setToggle(t.key, next)}
              />
            ))}
          </section>

          <section>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Notify on</div>
            {NOTIFY_TOGGLES.map((t) => (
              <ToggleRow
                key={t.key}
                label={t.label}
                value={cap[t.key] as boolean}
                disabled={update.isPending}
                onChange={(next) => setToggle(t.key, next)}
              />
            ))}
          </section>

          <section style={{ display: 'grid', gap: 8, maxWidth: 520 }}>
            <label style={{ display: 'grid', gap: 2 }}>
              <span style={{ fontSize: 12, color: '#888' }}>
                Notification recipients (comma-separated)
              </span>
              <input value={recipients} onChange={(e) => setRecipients(e.target.value)} />
            </label>
            <label style={{ display: 'grid', gap: 2 }}>
              <span style={{ fontSize: 12, color: '#888' }}>
                Authoritative outbound mailboxes (comma-separated)
              </span>
              <input value={mailboxes} onChange={(e) => setMailboxes(e.target.value)} />
            </label>
            <label style={{ display: 'grid', gap: 2, maxWidth: 200 }}>
              <span style={{ fontSize: 12, color: '#888' }}>Turn-scan lookback (days)</span>
              <input
                type="number"
                min={1}
                value={lookback}
                onChange={(e) => setLookback(e.target.value)}
              />
            </label>
            <div>
              <button type="button" disabled={update.isPending} onClick={saveLists}>
                Save recipients &amp; window
              </button>
            </div>
          </section>

          {settings.data?.updated_by && (
            <span className={styles.metaItem}>last set by {settings.data.updated_by}</span>
          )}
          {update.isError && (
            <span className={styles.error}>Could not update settings — try again.</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Archived panel (PR 6, collapsed by default, above Errors) ─────────────────

function ArchivedPanel({
  rows,
  onSelect,
  onUnarchive,
}: {
  rows: NdaDocketRow[];
  onSelect: (id: number) => void;
  onUnarchive: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.errorPanel}>
      <button
        type="button"
        className={styles.errorPanelToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.errorPanelBadge}>{rows.length}</span>
        Archived
        <span className={styles.errorPanelChevron}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className={styles.errorPanelBody}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Deal</th>
                <th>Status</th>
                <th>Archived</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={styles.docketRow}
                  onClick={() => onSelect(row.id)}
                >
                  <td className={styles.dealCell}>{row.deal_name}</td>
                  <td>
                    <StatusChip status={row.status} />
                  </td>
                  <td className={styles.mono}>
                    {fmtDateTime(row.archived_at)}
                    {row.archived_by ? ` · ${row.archived_by}` : ''}
                  </td>
                  <td>
                    <button
                      className={styles.downloadButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnarchive(row.id);
                      }}
                    >
                      Unarchive
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Error panel ──────────────────────────────────────────────────────────────

function ErrorPanel() {
  const errors = useNdaErrors();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const rows = errors.data?.errors ?? [];
  const count = rows.length;

  return (
    <div className={styles.errorPanel}>
      <button
        type="button"
        className={styles.errorPanelToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={count > 0 ? styles.errorPanelBadgeBad : styles.errorPanelBadge}>
          {count}
        </span>
        Errors &amp; attention items
        <span className={styles.errorPanelChevron}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className={styles.errorPanelBody}>
          {errors.isLoading && <div className={styles.loading}>Loading…</div>}
          {errors.error && (
            <div className={styles.error}>Could not load errors: {errors.error.message}</div>
          )}
          {!errors.isLoading && count === 0 && (
            <div className={styles.empty}>No errors or attention items. Clean.</div>
          )}
          {rows.map((e, i) => (
            <div key={i} className={styles.errorRow}>
              <div
                className={styles.errorRowHead}
                onClick={() => setExpanded(expanded === i ? null : i)}
              >
                <span className={styles.errorKind}>{errorKindLabel(e)}</span>
                <span className={styles.errorSummary}>{e.summary}</span>
                <span className={styles.errorWhen}>{fmtDateTime(e.occurred_at)}</span>
                {e.detail && (
                  <span className={styles.errorPanelChevron}>
                    {expanded === i ? '▾' : '▸'}
                  </span>
                )}
              </div>
              {expanded === i && e.detail && (
                <pre className={styles.errorDetail}>{prettyDetail(e.detail)}</pre>
              )}
              {expanded === i && (
                <div className={styles.errorMeta}>
                  {e.review_id != null && <span>review #{e.review_id}</span>}
                  {e.from_email && <span>from {e.from_email}</span>}
                  {e.subject && <span>subj: {e.subject}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function errorKindLabel(e: NdaErrorEntry): string {
  const map: Record<string, string> = {
    processing_failed: 'failed',
    spoofed_sender_rejected: 'spoof',
    message_needs_attention: 'attention',
    review_failed: 'review failed',
  };
  return map[e.kind] ?? e.kind;
}

function prettyDetail(detail: string): string {
  try {
    return JSON.stringify(JSON.parse(detail), null, 2);
  } catch {
    return detail;
  }
}

function DocketRow({
  row,
  onSelect,
  onArchive,
}: {
  row: NdaDocketRow;
  onSelect: (id: number) => void;
  onArchive: (id: number) => void;
}) {
  return (
    <tr className={styles.docketRow} onClick={() => onSelect(row.id)}>
      <td className={styles.dealCell}>
        {row.deal_name}
        {row.triggered_by === 'pipeline_auto' && (
          <span className={styles.originChip} title="Auto-detected by the NDA-intent pipeline">auto</span>
        )}
        {(row.counterparty_display_name || row.counterparty_org_id != null) && (
          <span className={styles.counterparty}>
            {' · '}{row.counterparty_display_name ?? `org ${row.counterparty_org_id}`}
          </span>
        )}
      </td>
      <td>
        <StatusChip status={row.status} />
      </td>
      <td>
        <WaitingOnChip waitingOn={row.waiting_on} />
      </td>
      <td>
        <DeliveryChip row={row} />
      </td>
      <td className={styles.mono}>
        {row.latest_turn_number != null
          ? `${row.latest_turn_number} · ${row.latest_turn_status ?? ''}`
          : '—'}
      </td>
      <td className={styles.mono}>
        {mustHaveProgressLabel(row.must_have_open, row.must_have_total)}
      </td>
      <td className={styles.mono}>{row.age_days}d</td>
      <td className={styles.mono}>{fmtDateTime(row.last_activity_at)}</td>
      <td>
        <button
          className={styles.downloadButton}
          onClick={(e) => {
            e.stopPropagation();
            onArchive(row.id);
          }}
        >
          Archive
        </button>
      </td>
    </tr>
  );
}

// ── Health strip ─────────────────────────────────────────────────────────────

function HealthStrip() {
  const health = useNdaHealth();
  const h = health.data;

  return (
    <div className={styles.healthStrip}>
      <div className={styles.healthCell}>
        <span className={styles.healthLabel}>Mailbox poller</span>
        <span className={styles.healthValue}>
          {!h
            ? '…'
            : !h.poller_schedule_found
              ? 'not scheduled'
              : h.poller_last_run_at
                ? `${fmtDateTime(h.poller_last_run_at)} · ${h.poller_last_status ?? '—'}`
                : 'never fired'}
        </span>
      </div>
      <div className={styles.healthCell}>
        <span className={styles.healthLabel}>Open reviews</span>
        <span className={styles.healthValue}>{h ? h.open_reviews : '…'}</span>
      </div>
      <div className={styles.healthCell}>
        <span className={styles.healthLabel}>Failed sessions (7d)</span>
        <span
          className={
            h && h.failed_sessions_7d > 0
              ? `${styles.healthValue} ${styles.healthBad}`
              : styles.healthValue
          }
        >
          {h ? h.failed_sessions_7d : '…'}
        </span>
      </div>
      <div className={styles.healthCell}>
        <span className={styles.healthLabel}>Oldest waiting</span>
        <span className={styles.healthValue}>
          {h
            ? h.oldest_open_review_age_days != null
              ? `${h.oldest_open_review_age_days}d`
              : '—'
            : '…'}
        </span>
      </div>
      <div className={styles.healthCell}>
        <span className={styles.healthLabel}>Awaiting NDA draft</span>
        <span
          className={styles.healthValue}
          title="Auto-detected execute-the-NDA intent with no draft turned into a review yet. A high count next to few reviews can mean draft detection is broken — investigate via Pipeline Insights → Events."
        >
          {h ? h.armed_awaiting_draft_count : '…'}
        </span>
      </div>
      <div className={styles.healthCell}>
        <span className={styles.healthLabel}>.doc converter</span>
        <span
          className={styles.healthValue}
          title="In-tenant legacy .doc→.docx converter reachability. Shown only when NDA .doc conversion is enabled; '—' means the feature is off."
        >
          {h
            ? h.doc_converter_reachable == null
              ? '—'
              : h.doc_converter_reachable
                ? 'OK'
                : 'down'
            : '…'}
        </span>
      </div>
    </div>
  );
}

// ── Review detail ────────────────────────────────────────────────────────────

function ReviewDetail({ reviewId, onBack }: { reviewId: number; onBack: () => void }) {
  const detail = useNdaReviewDetail(reviewId);
  const d = detail.data;
  const [showBindModal, setShowBindModal] = useState(false);
  const [showFindings, setShowFindings] = useState(false);
  const unbind = useUnbindReview();
  const isBound = d != null && (d.deal_kind === 'affinity_opp' || d.deal_kind === 'candidate');

  return (
    <div className={styles.root}>
      <button className={styles.backButton} onClick={onBack}>
        ← Back to docket
      </button>

      {detail.isLoading && <div className={styles.loading}>Loading review…</div>}
      {detail.error && (
        <div className={styles.error}>
          Could not load the review: {detail.error.message}
        </div>
      )}

      {d && (
        <>
          <div className={styles.header}>
            <h1 className={styles.title}>{d.deal_name}</h1>
            <StatusPicker reviewId={d.id} status={d.status} />
            <WaitingOnChip waitingOn={d.waiting_on} />
            <DeliveryChip row={d} />
          </div>
          <div className={styles.metaRow}>
            {isBound ? (
              <span className={styles.metaItem}>
                bound: {d.deal_kind === 'affinity_opp' ? `opp ${d.deal_key}` : `candidate ${d.deal_key}`}
                {' '}
                <button type="button" style={bindLinkBtn} onClick={() => setShowBindModal(true)}>
                  change
                </button>
                {' '}
                <button
                  type="button"
                  style={bindLinkBtn}
                  onClick={() => unbind.mutate(d.id)}
                  disabled={unbind.isPending}
                >
                  unbind
                </button>
                {unbind.isError && (
                  <span style={{ color: '#B00020', fontSize: 12 }}> unbind failed — try again</span>
                )}
              </span>
            ) : (
              <button type="button" style={bindLinkBtn} onClick={() => setShowBindModal(true)}>
                ＋ bind to deal
              </button>
            )}
            <NdaLinksEditor review={d} />
            {d.nda_processor_key && (
              <span className={styles.metaItem}>processor: {d.nda_processor_key}</span>
            )}
            {d.guidance_version_id != null && (
              <span className={styles.metaItem}>guidance v{d.guidance_version_id}</span>
            )}
            <span className={styles.metaItem}>opened {fmtDateTime(d.created_at)}</span>
            {d.created_by && <span className={styles.metaItem}>by {d.created_by}</span>}
            {d.triggered_by === 'pipeline_auto' && (
              <span className={styles.metaItem}>
                <span className={styles.originChip} title="Auto-detected by the NDA-intent pipeline">auto</span>
                {d.source && (
                  d.source.signal_id != null ? (
                    <a
                      className={styles.sourceLink}
                      href={deepLinks.insightsForSignal(d.source.signal_id)}
                      title={`${d.source.subject ?? ''} · ${d.source.sender ?? ''}`}
                    >
                      source: {d.source.subject || `event #${d.source.event_log_id}`}
                    </a>
                  ) : (
                    <span> source: event #{d.source.event_log_id}</span>
                  )
                )}
              </span>
            )}
          </div>

          <h2 className={styles.sectionTitle}>Activity</h2>
          {d.timeline.length === 0 ? (
            <div className={styles.empty}>No activity recorded yet.</div>
          ) : (
            <ul className={styles.timeline}>
              {d.timeline.map((entry, i) => (
                <TimelineRow
                  key={`${entry.kind}-${entry.occurred_at}-${entry.document_id ?? i}`}
                  entry={entry}
                  reviewId={d.id}
                />
              ))}
            </ul>
          )}

          <h2 className={styles.sectionTitle}>Documents</h2>
          {d.documents.length === 0 ? (
            <div className={styles.empty}>No documents captured yet.</div>
          ) : (
            <DocumentsList reviewId={d.id} documents={d.documents} />
          )}

          <h2 className={styles.sectionTitle}>
            Findings ledger
            {d.turns.length > 0 && (
              <span className={styles.sectionNote}>
                {' '}
                · turn {d.turns[d.turns.length - 1].turn_number}
              </span>
            )}
          </h2>
          {d.findings.length === 0 ? (
            <div className={styles.empty}>No findings on the latest turn.</div>
          ) : (
            <button type="button" style={actionBtn} onClick={() => setShowFindings(true)}>
              View findings ledger ({groupFindings(d.findings).length})
            </button>
          )}

          <h2 className={styles.sectionTitle}>Artifacts</h2>
          {d.artifacts.length === 0 ? (
            <div className={styles.empty}>No artifacts yet.</div>
          ) : (
            <ArtifactsList reviewId={d.id} artifacts={d.artifacts} />
          )}

          <h2 className={styles.sectionTitle}>Closing review — executed NDA &amp; final terms</h2>
          <ClosingReview reviewId={d.id} />

          {showBindModal && (
            <NdaBindModal reviewId={d.id} onClose={() => setShowBindModal(false)} />
          )}
          {showFindings && (
            <NdaFindingsModal
              findings={d.findings}
              turnNumber={d.turns.length ? d.turns[d.turns.length - 1].turn_number : null}
              onClose={() => setShowFindings(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

const bindLinkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#2a6',
  cursor: 'pointer',
  fontSize: 12,
  padding: 0,
  textDecoration: 'underline',
};

const actionBtn: React.CSSProperties = {
  background: 'none',
  border: '1px solid #ccc',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  marginRight: 6,
  padding: '2px 8px',
};

/**
 * Executed-NDA closing review (PR 6B-2). The active execution summary + its
 * source-anchored obligations, with confirm (all / selected), edit-then-confirm
 * (source evidence is immutable — only the structured projection is editable),
 * and revert. Extracted rows are NDA-only; confirmed rows become eligible for
 * future reminders/integrations.
 */
function ClosingReview({ reviewId }: { reviewId: number }) {
  const exec = useNdaExecution(reviewId);
  const confirmObligations = useConfirmObligations(reviewId);
  const confirmExecution = useConfirmExecution(reviewId);
  const revertExecution = useRevertExecution(reviewId);

  if (exec.isLoading) return <div className={styles.loading}>Loading closing review…</div>;
  if (exec.error) {
    return <div className={styles.error}>Could not load the closing review: {exec.error.message}</div>;
  }
  const data = exec.data;
  if (!data) return null;

  const { execution_summary: summary, obligations, lifecycle_status } = data;
  if (!summary && obligations.length === 0) {
    return <div className={styles.empty}>No executed copy captured yet.</div>;
  }
  const extractedCount = obligations.filter((o) => o.status === 'extracted').length;

  return (
    <div>
      {summary && (
        <div className={styles.metaRow} style={{ flexWrap: 'wrap', gap: 8 }}>
          <span className={styles.metaItem}>
            status: <strong>{summary.status}</strong>
            {summary.confirmed_by && ` · by ${summary.confirmed_by}`}
          </span>
          {summary.counterparty_legal_name && (
            <span className={styles.metaItem}>counterparty: {summary.counterparty_legal_name}</span>
          )}
          {summary.ember_legal_name && (
            <span className={styles.metaItem}>Ember entity: {summary.ember_legal_name}</span>
          )}
          {summary.signatory_name && (
            <span className={styles.metaItem}>
              signatory: {summary.signatory_name}
              {summary.signatory_title && ` (${summary.signatory_title})`}
            </span>
          )}
          {summary.executed_on && (
            <span className={styles.metaItem}>executed {fmtDate(summary.executed_on)}</span>
          )}
          {summary.agreement_term && (
            <span className={styles.metaItem}>term: {summary.agreement_term}</span>
          )}
          {summary.expiration_date && (
            <span className={styles.metaItem}>expires {fmtDate(summary.expiration_date)}</span>
          )}
        </div>
      )}

      <div style={{ margin: '8px 0' }}>
        {lifecycle_status === 'executed_unconfirmed' && (
          <button
            type="button"
            style={actionBtn}
            onClick={() => confirmExecution.mutate()}
            disabled={confirmExecution.isPending}
          >
            Confirm executed copy
          </button>
        )}
        {(lifecycle_status === 'executed_unconfirmed' || lifecycle_status === 'executed') && (
          <button
            type="button"
            style={actionBtn}
            onClick={() => revertExecution.mutate()}
            disabled={revertExecution.isPending}
          >
            Revert execution
          </button>
        )}
        {extractedCount > 0 && (
          <button
            type="button"
            style={actionBtn}
            onClick={() => confirmObligations.mutate(undefined)}
            disabled={confirmObligations.isPending}
          >
            Confirm all {extractedCount} extracted
          </button>
        )}
        {(confirmExecution.isError || revertExecution.isError || confirmObligations.isError) && (
          <span className={styles.error} style={{ fontSize: 12 }}> action failed — try again</span>
        )}
      </div>

      {obligations.length === 0 ? (
        <div className={styles.empty}>No obligations extracted from the executed document.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Provision</th>
              <th>Binds</th>
              <th>Duration</th>
              <th>Expires</th>
              <th>Conf.</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {obligations.map((ob) => (
              <ObligationRow key={ob.id} reviewId={reviewId} ob={ob} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const OB_BINDS = ['ember', 'counterparty', 'mutual'] as const;

function ObligationRow({ reviewId, ob }: { reviewId: number; ob: NdaObligationRow }) {
  const [editing, setEditing] = useState(false);
  const confirmObligations = useConfirmObligations(reviewId);
  const revertObligation = useRevertObligation(reviewId);
  const editObligation = useEditObligation(reviewId);

  return (
    <>
      <tr>
        <td className={styles.mono}>
          {ob.provision_key}
          {ob.edited && <span className={styles.sectionNote}> · edited</span>}
        </td>
        <td className={styles.mono}>{ob.binds ?? '—'}</td>
        <td className={styles.mono}>{ob.duration_label ?? '—'}</td>
        <td className={styles.mono}>{ob.expires_on ? fmtDate(ob.expires_on) : '—'}</td>
        <td className={styles.mono}>{ob.extraction_confidence ?? '—'}</td>
        <td className={styles.mono}>
          {ob.status}
          {ob.status === 'confirmed' && ob.confirmed_by && (
            <span className={styles.sectionNote}> · {ob.confirmed_by}</span>
          )}
        </td>
        <td>
          {ob.status === 'extracted' && (
            <button
              type="button"
              style={actionBtn}
              onClick={() => confirmObligations.mutate([ob.id])}
              disabled={confirmObligations.isPending}
            >
              Confirm
            </button>
          )}
          {ob.status === 'confirmed' && (
            <button
              type="button"
              style={actionBtn}
              onClick={() => revertObligation.mutate(ob.id)}
              disabled={revertObligation.isPending}
            >
              Un-confirm
            </button>
          )}
          <button type="button" style={actionBtn} onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
          {(confirmObligations.isError || revertObligation.isError) && (
            <span className={styles.error} style={{ fontSize: 12 }}> failed — retry</span>
          )}
        </td>
      </tr>
      {ob.obligation_text && (
        <tr>
          <td colSpan={7} className={styles.mono} style={{ color: '#666', fontSize: 12, paddingLeft: 16 }}>
            “{ob.obligation_text}”
          </td>
        </tr>
      )}
      {editing && (
        <tr>
          <td colSpan={7}>
            <ObligationEditForm
              ob={ob}
              pending={editObligation.isPending}
              error={editObligation.isError}
              onCancel={() => setEditing(false)}
              onSave={(edit) =>
                editObligation.mutate(
                  { obligationId: ob.id, edit },
                  { onSuccess: () => setEditing(false) },
                )
              }
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ObligationEditForm({
  ob,
  pending,
  error,
  onCancel,
  onSave,
}: {
  ob: NdaObligationRow;
  pending: boolean;
  error: boolean;
  onCancel: () => void;
  onSave: (edit: NdaObligationEdit) => void;
}) {
  const [binds, setBinds] = useState(ob.binds ?? '');
  const [duration, setDuration] = useState(ob.duration_label ?? '');
  const [expires, setExpires] = useState(ob.expires_on ? ob.expires_on.slice(0, 10) : '');
  const [summary, setSummary] = useState(ob.obligation_summary ?? '');

  const save = () => {
    // Only send changed fields (backend keys off model_fields_set for the diff).
    const edit: NdaObligationEdit = {};
    if (binds !== (ob.binds ?? '')) edit.binds = binds;
    if (duration !== (ob.duration_label ?? '')) edit.duration_label = duration;
    if (expires !== (ob.expires_on ? ob.expires_on.slice(0, 10) : '')) edit.expires_on = expires;
    if (summary !== (ob.obligation_summary ?? '')) edit.obligation_summary = summary;
    onSave(edit);
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '4px 16px' }}>
      <label style={editLabel}>
        binds{' '}
        <select value={binds} onChange={(e) => setBinds(e.target.value)}>
          <option value="">—</option>
          {OB_BINDS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </label>
      <label style={editLabel}>
        duration{' '}
        <input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 2 years" />
      </label>
      <label style={editLabel}>
        expires{' '}
        <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
      </label>
      <label style={{ ...editLabel, flex: '1 1 240px' }}>
        summary{' '}
        <input
          style={{ width: '100%' }}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
        />
      </label>
      <button type="button" style={actionBtn} onClick={save} disabled={pending}>Save</button>
      <button type="button" style={actionBtn} onClick={onCancel}>Cancel</button>
      {error && <span className={styles.error} style={{ fontSize: 12 }}>save failed</span>}
    </div>
  );
}

const editLabel: React.CSSProperties = { fontSize: 12, color: '#555' };

function TimelineRow({ entry, reviewId }: { entry: NdaTimelineEntry; reviewId: number }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const canDownload = entry.document_id != null && entry.download_ok === true;
  const download = async () => {
    if (entry.document_id == null) return;
    setBusy(true);
    setErr(false);
    try {
      await downloadNdaDocument(reviewId, entry.document_id, entry.document_filename ?? null);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className={styles.timelineRow}>
      <span className={styles.timelineWhen}>{fmtDateTime(entry.occurred_at)}</span>
      <span className={`${styles.timelineKind} ${kindClass(entry.kind)}`}>{entry.kind}</span>
      <span className={styles.timelineSummary}>{entry.summary}</span>
      {canDownload && (
        <button type="button" style={bindLinkBtn} disabled={busy} onClick={download}>
          {busy ? 'downloading…' : `↓ ${entry.document_filename || 'document'}`}
        </button>
      )}
      {err && <span className={styles.error}>download failed</span>}
    </li>
  );
}

function kindClass(kind: NdaTimelineEntry['kind']): string {
  if (kind === 'email') return styles.kindEmail;
  if (kind === 'session') return styles.kindSession;
  if (kind === 'lifecycle') return styles.kindSession;
  return styles.kindAudit;
}

function DocumentsList({
  reviewId,
  documents,
}: {
  reviewId: number;
  documents: NdaDocument[];
}) {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // Executed copy pinned first (Terms), then newest version first.
  const ordered = [...documents].sort((a, b) => {
    if (a.is_executed !== b.is_executed) return a.is_executed ? -1 : 1;
    return (b.version_number ?? 0) - (a.version_number ?? 0);
  });
  const handleDownload = async (doc: NdaDocument) => {
    setBusyId(doc.id);
    setDownloadError(null);
    try {
      await downloadNdaDocument(reviewId, doc.id, doc.filename);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setBusyId(null);
    }
  };
  return (
    <>
      {downloadError && <div className={styles.error}>{downloadError}</div>}
      <ul className={styles.artifactList}>
        {ordered.map((doc) => (
          <li key={doc.id} className={styles.artifactRow}>
            <span className={styles.artifactType}>
              {doc.is_executed ? '★ executed' : doc.version_kind || 'document'}
            </span>
            <span className={styles.artifactName}>{doc.filename ?? `document ${doc.id}`}</span>
            <span className={styles.mono}>
              {doc.version_number != null ? `v${doc.version_number}` : ''}
              {doc.sha256_short ? ` · sha ${doc.sha256_short}` : ''}
            </span>
            <span className={styles.mono}>{fmtDateTime(doc.created_at)}</span>
            {doc.download_ok ? (
              <button
                className={styles.downloadButton}
                disabled={busyId === doc.id}
                onClick={() => handleDownload(doc)}
              >
                {busyId === doc.id ? 'Downloading…' : 'Download'}
              </button>
            ) : (
              <span className={styles.mono} title="bytes not stored / not downloadable">
                —
              </span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

// ── Findings ─────────────────────────────────────────────────────────────────
//
// The ledger stores ONE nda_finding row per anchored edit, so a single logical
// finding that needs several edits produces several rows with identical
// narrative (status/severity/draft_says/concern/recommendation). Group those
// into one card and list the per-edit anchor/replacement/comment underneath, so
// the shared narrative isn't repeated.

interface FindingEdit {
  id: number;
  anchor_text: string | null;
  anchor_occurrence: number | null;
  replacement_text: string | null;
  comment_text: string | null;
}

interface FindingGroup {
  key: string;
  guide_section: string;
  status: string;
  severity: string | null;
  resolution: string;
  concern: string | null;
  draft_says: string | null;
  recommendation: string | null;
  escalation_reason: string | null;
  edits: FindingEdit[];
}

function groupFindings(findings: NdaFindingRow[]): FindingGroup[] {
  const order: FindingGroup[] = [];
  const byKey = new Map<string, FindingGroup>();
  for (const f of findings) {
    // Same narrative = same logical finding; only the anchor/replacement differ.
    const key = JSON.stringify([
      f.guide_section, f.status, f.severity, f.resolution,
      f.draft_says, f.concern, f.recommendation, f.escalation_reason,
    ]);
    let g = byKey.get(key);
    if (!g) {
      g = {
        key, guide_section: f.guide_section, status: f.status, severity: f.severity,
        resolution: f.resolution, concern: f.concern, draft_says: f.draft_says,
        recommendation: f.recommendation, escalation_reason: f.escalation_reason,
        edits: [],
      };
      byKey.set(key, g);
      order.push(g);
    }
    if (f.anchor_text || f.replacement_text || f.comment_text) {
      g.edits.push({
        id: f.id, anchor_text: f.anchor_text, anchor_occurrence: f.anchor_occurrence,
        replacement_text: f.replacement_text, comment_text: f.comment_text,
      });
    }
  }
  return order;
}

// Display order mirrors the email memo bucketing in artifacts.memo_markdown
// (operator request 2026-06-18): provisions outside the guide elevated above
// must-haves, then escalations, standard, recommended, acceptable. Stable within
// a rank (preserves source order). The active-issue checks come FIRST so an
// out-of-guide finding that IS an active issue ranks with its severity (mirrors
// the backend elif chain); only a non-active out-of-guide provision — status
// 'flag_other' OR section keyed 'other' — is elevated to the top.
function categoryRank(g: FindingGroup): number {
  if (g.status === 'escalate_to_outside_counsel') return 2;
  const needsWork = g.status === 'needs_revision' || g.status === 'missing';
  if (needsWork && g.severity === 'must_have') return 1;
  if (needsWork && g.severity === 'standard') return 3;
  if (needsWork) return 4;
  if (g.status === 'flag_other' || g.guide_section === 'other') return 0;
  return 5;
}

// Category label for the ledger's "Category" column (mirrors categoryRank).
function categoryLabel(g: FindingGroup): string {
  const rank = categoryRank(g);
  return (
    ['Out-of-guide provision', 'Must-have issue', 'Escalation', 'Standard edit',
      'Other issue', 'Recommended / acceptable'][rank] ?? 'Finding'
  );
}

// Disposition chip: must_have/standard land in the turn-1 redline (DONE);
// recommended is memo-only for Peter to opt into (TO CONSIDER). Severity is the
// only signal (product decision). Escalations get their own marker.
function dispositionChip(g: FindingGroup): { label: string; done: boolean } | null {
  if (g.status === 'escalate_to_outside_counsel') return { label: 'ESCALATE', done: false };
  // DONE = an ACTIVE must-have/standard issue (what actually lands in the turn-1
  // redline). A non-active status (acceptable/flag_other) never reads DONE even at
  // must_have/standard severity — the edit wasn't applied.
  const active = g.status === 'needs_revision' || g.status === 'missing';
  if (active && (g.severity === 'must_have' || g.severity === 'standard')) {
    return { label: 'DONE', done: true };
  }
  if (g.severity === 'recommended') return { label: 'TO CONSIDER', done: false };
  return null;
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px',
  overflow: 'auto',
};
const modalPanel: React.CSSProperties = {
  background: 'var(--surface, #fff)', color: 'inherit', borderRadius: 10,
  width: 'min(1100px, 100%)', maxHeight: '90vh', overflow: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
};

function NdaFindingsModal({
  findings,
  turnNumber,
  onClose,
}: {
  findings: NdaFindingRow[];
  turnNumber: number | null;
  onClose: () => void;
}) {
  const groups = [...groupFindings(findings)].sort((a, b) => categoryRank(a) - categoryRank(b));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  return (
    <div style={modalBackdrop} onClick={onClose} role="dialog" aria-modal="true">
      <div style={modalPanel} onClick={(e) => e.stopPropagation()}>
        <div
          style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '14px 18px',
            borderBottom: '1px solid var(--border, #e5e1d8)', position: 'sticky', top: 0,
            background: 'var(--surface, #fff)' }}
        >
          <strong style={{ fontSize: 16 }}>Findings ledger</strong>
          {turnNumber != null && (
            <span className={styles.sectionNote}>turn {turnNumber} · {groups.length} findings</span>
          )}
          <button type="button" style={{ ...actionBtn, marginLeft: 'auto' }} onClick={onClose}>
            Close ✕
          </button>
        </div>
        <div style={{ padding: '4px 18px 18px', overflowX: 'auto' }}>
          <table className={styles.table} style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '16%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '56%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Section</th>
                <th>Category</th>
                <th>Severity</th>
                <th>Concern &amp; Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <FindingLedgerRow
                  key={g.key}
                  group={g}
                  expanded={expanded.has(g.key)}
                  onToggle={() => toggle(g.key)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FindingLedgerRow({
  group,
  expanded,
  onToggle,
}: {
  group: FindingGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  const g = group;
  const { concern, bullets } = splitRecommendation(g.concern, g.recommendation);
  const chip = dispositionChip(g);
  return (
    <>
      <tr className={styles.findingRow} onClick={onToggle} style={{ verticalAlign: 'top' }}>
        <td>
          <strong>{g.guide_section}</strong>
          {g.edits.length > 1 && (
            <span className={styles.sectionNote}> · {g.edits.length} edits</span>
          )}
        </td>
        <td className={styles.mono}>{categoryLabel(g)}</td>
        <td>
          {g.severity && (
            <span
              className={
                g.severity === 'must_have'
                  ? `${styles.severityBadge} ${styles.severityMustHave}`
                  : g.severity === 'standard'
                    ? `${styles.severityBadge} ${styles.severityStandard}`
                    : styles.severityBadge
              }
            >
              {g.severity.replace('_', ' ')}
            </span>
          )}
        </td>
        <td>
          {chip && (
            <span
              style={{
                display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 4,
                padding: '0 6px', marginBottom: 4,
                background: chip.done ? '#166534' : '#92400e', color: '#fff',
              }}
            >
              {chip.label}
            </span>
          )}
          {concern && <div style={{ marginBottom: 4 }}>{concern}</div>}
          {bullets.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
          {!concern && bullets.length === 0 && <span className={styles.mono}>—</span>}
        </td>
      </tr>
      {expanded && (
        <tr className={styles.findingDetailRow}>
          <td colSpan={4}>
            <div className={styles.findingDetail}>
              {g.draft_says && <FindingField label="Draft says" value={g.draft_says} />}
              {g.edits.length > 0 && (
                <div>
                  <span className={styles.findingFieldLabel}>Proposed edits (old → new)</span>
                  <ol className={styles.moveList}>
                    {g.edits.map((e) => (
                      <li key={e.id} className={styles.moveItem}>
                        {(e.anchor_text || e.replacement_text) && (
                          <WordDiffView oldText={e.anchor_text} newText={e.replacement_text} />
                        )}
                        {e.comment_text && <FindingField label="Comment" value={e.comment_text} />}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {g.escalation_reason && (
                <FindingField label="Escalation" value={g.escalation_reason} />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Inline word-level old→new diff (deletions struck, insertions highlighted).
function WordDiffView({
  oldText,
  newText,
}: {
  oldText: string | null;
  newText: string | null;
}) {
  const tokens = wordDiff(oldText, newText);
  return (
    <div className={styles.findingFieldValue} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
      {tokens.map((t, i) => {
        if (t.type === 'same') return <span key={i}>{t.text}</span>;
        if (t.type === 'del')
          return (
            <span key={i} style={{ background: '#fde2e1', color: '#7f1d1d', textDecoration: 'line-through' }}>
              {t.text}
            </span>
          );
        return (
          <span key={i} style={{ background: '#dcfce7', color: '#166534' }}>
            {t.text}
          </span>
        );
      })}
    </div>
  );
}

function FindingField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.findingField}>
      <span className={styles.findingFieldLabel}>{label}</span>
      <span className={styles.findingFieldValue}>{value}</span>
    </div>
  );
}

// ── Artifacts ────────────────────────────────────────────────────────────────

function ArtifactsList({
  reviewId,
  artifacts,
}: {
  reviewId: number;
  artifacts: NdaArtifactRow[];
}) {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (artifact: NdaArtifactRow) => {
    setBusyId(artifact.id);
    setDownloadError(null);
    try {
      await downloadNdaArtifact(reviewId, artifact.id, artifact.filename);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {downloadError && <div className={styles.error}>{downloadError}</div>}
      <ul className={styles.artifactList}>
        {artifacts.map((a) => (
          <li key={a.id} className={styles.artifactRow}>
            <span className={styles.artifactType}>{a.artifact_type}</span>
            <span className={styles.artifactName}>{a.filename ?? `artifact ${a.id}`}</span>
            <span className={styles.mono}>
              {a.sha256_short ? `sha ${a.sha256_short}` : ''}
              {a.ledger_hash_short ? ` · ledger ${a.ledger_hash_short}` : ''}
            </span>
            {a.status && <span className={styles.mono}>{a.status}</span>}
            <span className={styles.mono}>{fmtDateTime(a.created_at)}</span>
            <button
              className={styles.downloadButton}
              disabled={busyId === a.id}
              onClick={() => handleDownload(a)}
            >
              {busyId === a.id ? 'Downloading…' : 'Download'}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

// ── Chips + formatting ───────────────────────────────────────────────────────

// Friendly labels for the status vocabulary (executed_unconfirmed spells itself out).
const STATUS_LABEL: Record<string, string> = {
  open: 'open',
  executed_unconfirmed: 'executed (unconfirmed)',
  executed: 'executed',
  concluded: 'concluded',
  dismissed: 'dismissed',
};

function StatusChip({ status }: { status: string }) {
  const cls =
    status === 'open'
      ? styles.statusOpen
      : status === 'executed' || status === 'executed_unconfirmed'
        ? styles.statusExecuted
        : status === 'concluded'
          ? styles.statusConcluded
          : styles.statusDismissed;
  return <span className={`${styles.chip} ${cls}`}>{STATUS_LABEL[status] ?? status}</span>;
}

// PR-1: executed_unconfirmed is now a valid manual target (the picker matches the
// lifecycle enum). Setting it emits a manual_correction → phase=execution,
// waiting=ember (see routers/nda._manual_status_facets).
const NDA_STATUSES = ['open', 'executed_unconfirmed', 'executed', 'concluded', 'dismissed'];

// Editable lifecycle picker for the detail header (manual status override).
// status-only — executed_on capture stays the email loop's job (backend).
function StatusPicker({ reviewId, status }: { reviewId: number; status: string }) {
  const update = useUpdateReviewStatus();
  return (
    <span className={styles.metaItem}>
      <select
        aria-label="Review status"
        value={status}
        disabled={update.isPending}
        onChange={(e) => update.mutate({ id: reviewId, status: e.target.value })}
      >
        {NDA_STATUSES.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABEL[s] ?? s}
          </option>
        ))}
      </select>
      {update.isError && (
        <span style={{ color: '#B00020', fontSize: 12 }}> status update failed</span>
      )}
    </span>
  );
}

const ROLE_FRAMINGS = ['receiving', 'disclosing', 'mutual', 'unclear'];

// Editable CRM links (PR-2b): counterparty + counsel via the org picker, role
// framing via a select. Each edit locks the field so the auto-resolver won't
// clobber it. Displays the resolved names; the org picker searches the CRM layer.
function NdaLinksEditor({ review }: { review: NdaReviewDetail }) {
  const [editing, setEditing] = useState<null | 'counterparty' | 'counsel'>(null);
  const update = useUpdateReviewLinks(review.id);
  const cpName = review.counterparty_display_name
    ?? (review.counterparty_org_id != null ? `org ${review.counterparty_org_id}` : '—');
  const counselName = review.counterparty_law_firm_name
    ?? (review.counterparty_law_firm_org_id != null ? `org ${review.counterparty_law_firm_org_id}` : '—');
  const roleLabel = review.role_framing_text || review.role_framing || 'unclear';

  return (
    <>
      <span className={styles.metaItem}>
        counterparty: {cpName}{' '}
        <button type="button" className={styles.linkEditBtn} disabled={update.isPending}
          onClick={() => setEditing('counterparty')}>edit</button>
      </span>
      <span className={styles.metaItem}>
        counsel: {counselName}{' '}
        <button type="button" className={styles.linkEditBtn} disabled={update.isPending}
          onClick={() => setEditing('counsel')}>edit</button>
      </span>
      <span className={styles.metaItem}>
        role:{' '}
        <select aria-label="Role framing" value={review.role_framing || 'unclear'}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ role_framing: e.target.value })}>
          {ROLE_FRAMINGS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {review.role_framing_text && (
          <span style={{ color: '#888' }}> · {roleLabel}</span>
        )}
      </span>
      {update.isError && (
        <span style={{ color: '#B00020', fontSize: 12 }}> link update failed</span>
      )}
      {editing && (
        <NdaOrgPickModal
          title={editing === 'counterparty' ? 'Set counterparty org' : 'Set counsel (law firm)'}
          busy={update.isPending}
          onPick={(org) => {
            const field = editing === 'counterparty' ? 'counterparty_org_id' : 'counterparty_law_firm_org_id';
            update.mutate({ [field]: org.org_id }, { onSuccess: () => setEditing(null) });
          }}
          onClear={() => {
            const field = editing === 'counterparty' ? 'counterparty_org_id' : 'counterparty_law_firm_org_id';
            update.mutate({ [field]: null }, { onSuccess: () => setEditing(null) });
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function WaitingOnChip({ waitingOn }: { waitingOn: NdaWaitingOn | null }) {
  const cls =
    waitingOn === 'ember'
      ? styles.waitingPeter
      : waitingOn === 'counterparty'
        ? styles.waitingCounterparty
        : waitingOn === 'none'
          ? styles.waitingDone
          : styles.waitingAssociate; // system / null → neutral "processing"
  return <span className={`${styles.chip} ${cls}`}>{waitingOnLabel(waitingOn)}</span>;
}

// Delivery truth (PR 0A): the initial packet's honest state, distinct from
// the turn status. A queued packet is never shown as delivered.
const DELIVERY_LABEL: Record<NdaDeliveryState, string> = {
  delivered: 'Delivered',
  queued: 'Queued',
  not_ready: 'Not sent',
};

function recipientNames(to: string[], cc: string[]): string {
  return [...to, ...cc]
    .map((e) => e.split('@')[0])
    .map((n) => (n ? n.charAt(0).toUpperCase() + n.slice(1) : n))
    .join(' + ');
}

function DeliveryChip({
  row,
}: {
  row: Pick<NdaDocketRow, 'initial_delivery_state' | 'packet_to' | 'packet_cc'>;
}) {
  const state = row.initial_delivery_state;
  if (!state) return <span className={styles.mono}>—</span>;
  const cls =
    state === 'delivered'
      ? styles.deliveryDelivered
      : state === 'queued'
        ? styles.deliveryQueued
        : styles.deliveryNotReady;
  const to = row.packet_to ?? [];
  const cc = row.packet_cc ?? [];
  const who = recipientNames(to, cc);
  // Show recipients on the states where they matter (delivered/queued).
  const label =
    (state === 'delivered' || state === 'queued') && who
      ? `${DELIVERY_LABEL[state]} → ${who}`
      : DELIVERY_LABEL[state];
  const title = who
    ? `To: ${to.join(', ')}${cc.length ? ` · Cc: ${cc.join(', ')}` : ''}`
    : undefined;
  return (
    <span className={`${styles.chip} ${cls}`} title={title}>
      {label}
    </span>
  );
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
